import React from "react";
import { X } from "lucide-react";
import { useLanguage, useT } from "@/lib/LanguageContext";
import {
  CONSENT, CONSENT_KEYS, INACTIVE_DAYS, INACTIVE_KEYS, PACK_FILTERS, PACK_FILTER_KEYS,
  PURCHASE_KIND_KEYS, SOURCES, SOURCE_KEYS, STAGES, STAGE_KEYS,
} from "../contract";
import { fmtDate } from "../format";
import { cn } from "../../../lib/utils";
import CrmButton from "../ui/CrmButton";
import { FOCUS_RING } from "../ui/classes";
import FilterChip from "./FilterChip";
import { CRM } from "../../../constants/testIds/crm";

/**
 * The Clients filter bar (spec): Stage, Tags, Source (multi-select), Last activity,
 * Pack status, Consent (dropdowns), Clear filters. Filters that arrive from a dashboard
 * drill-down (bought in a period, created in a period) have no dropdown: they render as
 * removable chips.
 */
export default function ClientFilterBar({ filters, setFilter, clear, tags, isFiltered }) {
  const t = useT("crm");
  const { lang } = useLanguage();
  const join = (values, keys) => values.map((v) => (keys ? t(keys[v]) : v)).join(", ");
  const range = (from, to) => [from && fmtDate(from, lang), to && fmtDate(to, lang)].filter(Boolean).join(" – ");

  const multi = (key, label, values, keys) => (
    <FilterChip
      key={key}
      multi
      label={label}
      summary={filters[key].length ? t("filters.set", { label, value: join(filters[key], keys) }) : label}
      active={filters[key].length > 0}
      options={values.map((v) => ({ value: v, label: keys ? t(keys[v]) : v }))}
      value={filters[key]}
      onChange={(v) => setFilter(key, v)}
      testId={CRM.clientsFilter(key)}
      optionTestId={(v) => CRM.clientsFilterOption(key, v)}
    />
  );
  // `chip` is the chip's own id when it differs from the query key (Last activity reads
  // `inactive_days`; its chip is `last_activity`, CR-T-87).
  const single = (key, label, anyLabel, values, keyOf, showAnyInSummary, chip = key) => {
    const value = filters[key];
    const current = value ? t(keyOf(value)) : anyLabel;
    return (
      <FilterChip
        key={key}
        label={label}
        summary={value || showAnyInSummary ? t("filters.set", { label, value: current }) : label}
        active={!!value}
        options={[{ value: "", label: anyLabel }, ...values.map((v) => ({ value: String(v), label: t(keyOf(v)) }))]}
        value={value}
        onChange={(v) => setFilter(key, v)}
        testId={CRM.clientsFilter(chip)}
        optionTestId={(v) => CRM.clientsFilterOption(chip, v)}
      />
    );
  };

  const deepLinks = [];
  if (filters.purchased_kind || filters.purchased_from || filters.purchased_to) {
    deepLinks.push({
      key: "purchased",
      label: t("filters.bought", {
        kind: filters.purchased_kind && PURCHASE_KIND_KEYS[filters.purchased_kind] ? t(PURCHASE_KIND_KEYS[filters.purchased_kind]) : t("filters.anything"),
        range: range(filters.purchased_from, filters.purchased_to),
      }),
      remove: () => { setFilter("purchased_kind", ""); setFilter("purchased_from", ""); setFilter("purchased_to", ""); },
    });
  }
  if (filters.created_from || filters.created_to) {
    deepLinks.push({
      key: "created",
      label: t("filters.created", { range: range(filters.created_from, filters.created_to) }),
      remove: () => { setFilter("created_from", ""); setFilter("created_to", ""); },
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label={t("filters.label")}>
      {multi("stage", t("filters.stage"), STAGES, STAGE_KEYS)}
      {multi("tags", t("filters.tags"), tags)}
      {multi("source", t("filters.source"), SOURCES, SOURCE_KEYS)}
      {single("inactive_days", t("filters.lastActivity"), t("filters.any"), INACTIVE_DAYS, (v) => INACTIVE_KEYS[v], true, "last_activity")}
      {single("pack_status", t("filters.packStatus"), t("filters.all"), PACK_FILTERS, (v) => PACK_FILTER_KEYS[v], false)}
      {single("consent", t("filters.consent"), t("filters.all"), CONSENT, (v) => CONSENT_KEYS[v], false)}
      {deepLinks.map((d) => (
        <span key={d.key} data-testid={CRM.clientsFilter(d.key)}
          className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-xs text-eh-ink">
          {d.label}
          <button type="button" onClick={d.remove} aria-label={t("filters.remove", { label: d.label })}
            className={cn("rounded-full text-eh-muted hover:text-eh-ink", FOCUS_RING)}>
            <X size={12} aria-hidden="true" />
          </button>
        </span>
      ))}
      {isFiltered && (
        <CrmButton variant="link" size="sm" onClick={clear} data-testid={CRM.clientsClear}>{t("filters.clear")}</CrmButton>
      )}
    </div>
  );
}
